import { FC, useMemo, useState } from 'react';
import { CircleCloseIcon } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import CustomModal from 'components/custom-modal/CustomModal';
import { AssignableMember } from 'containers/jobs/jobs.model';
import ServiceAreaForm from 'containers/settings/service-areas/components/ServiceAreaForm';
import { SERVICE_AREA_COLORS, ServiceAreaStatus, ServiceAreaType } from 'containers/settings/service-areas/constants';
import { CreateServiceAreaFormData } from 'containers/settings/service-areas/serviceAreas.model';
// eslint-disable-next-line max-len
import { usePaginatedGetServiceAreasQuery } from 'containers/settings/service-areas/hooks/usePaginatedGetServiceAreasQuery';
import { If, Loader } from 'components';

interface ServiceAreaCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateServiceAreaFormData) => void;
  isLoading?: boolean;
}

const ServiceAreaCreateModal: FC<ServiceAreaCreateModalProps> = ({ isOpen, onClose, onSave, isLoading }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState(SERVICE_AREA_COLORS[0]);
  const [selectedMembers, setSelectedMembers] = useState<AssignableMember[]>([]);
  const [shapeData, setShapeData] = useState<{
    type: ServiceAreaType;
    data: number[][] | { centroid: [number, number]; range: number };
  } | null>(null);
  const [errors, setErrors] = useState<{ name?: string; shape?: string }>({});
  const [isAssignMembersModalOpen, setIsAssignMembersModalOpen] = useState(false);

  // Fetch all enabled service areas
  const { list: existingServiceAreas, isLoading: isLoadingExistingAreas } = usePaginatedGetServiceAreasQuery(null, {
    status: ServiceAreaStatus.ENABLED
  });

  // Transform existing service areas into the format expected by the map
  const existingShapes = useMemo(
    () =>
      existingServiceAreas?.map(area => ({
        type: area.type as ServiceAreaType,
        vertices: area.type === ServiceAreaType.POLYGON ? area.vertices : undefined,
        centroid: area.type === ServiceAreaType.CIRCLE ? area.centroid : undefined,
        range: area.type === ServiceAreaType.CIRCLE ? area.range : undefined,
        color: area.colorCode
      })),
    [existingServiceAreas]
  );

  const handleSave = () => {
    const newErrors: { name?: string; shape?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Please enter a name for the service area';
    }

    if (
      !shapeData ||
      (shapeData.type === ServiceAreaType.POLYGON && (shapeData?.data as [number, number][]).length === 1)
    ) {
      newErrors.shape = 'Please draw a service area on the map';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      name,
      color,
      members: selectedMembers,
      shape: shapeData
    });

    // Reset form
    handleFormReset();
    onClose();
  };

  const handleFormReset = () => {
    setName('');
    setColor(SERVICE_AREA_COLORS[0]);
    setSelectedMembers([]);
    setShapeData(null);
    setErrors({});
  };

  const handleSetName = (value: string) => {
    setName(value);
    setErrors(prev => ({ ...prev, name: undefined }));
  };

  const handleSetShapeData = (value: {
    type: ServiceAreaType;
    data: number[][] | { centroid: [number, number]; range: number };
  }) => {
    setShapeData(value);
    setErrors(prev => ({ ...prev, shape: undefined }));
  };

  return (
    <CustomModal
      open={isOpen}
      closeModal={onClose}
      width="w-full md:w-[90%] 2xl:w-[80%]"
      height="h-auto"
      className="p-6"
      enableOutsideClick={!isAssignMembersModalOpen}>
      <div className="flex flex-col">
        <div className="relative mb-6 flex items-center">
          <h2 className="text-xl font-bold text-headingGray">Add Service Area</h2>
          <CircleCloseIcon className="absolute right-0 top-0 cursor-pointer" onClick={onClose} />
        </div>

        <If condition={isLoadingExistingAreas}>
          <div className="flex h-full min-h-[50vh] items-center justify-center">
            <Loader />
          </div>
        </If>

        <If condition={!isLoadingExistingAreas}>
          <ServiceAreaForm
            name={name}
            color={color}
            selectedMembers={selectedMembers}
            onNameChange={handleSetName}
            onColorChange={setColor}
            onMembersChange={setSelectedMembers}
            onShapeComplete={handleSetShapeData}
            errors={errors}
            onAssignMembersOpen={() => setIsAssignMembersModalOpen(true)}
            onAssignMembersClose={() => setIsAssignMembersModalOpen(false)}
            existingShapes={existingShapes}
          />

          <div className="mt-6 flex justify-end gap-4">
            <CustomButton
              id="cancel-button"
              type={ButtonType.SECONDARY}
              childrenContainerStyles="text-[17px] font-semibold"
              className="h-10"
              onClick={() => {
                handleFormReset();
                onClose();
              }}>
              Cancel
            </CustomButton>
            <CustomButton
              id="save-button"
              type={ButtonType.PRIMARY}
              childrenContainerStyles="text-[17px] font-semibold"
              className="h-10"
              isLoading={isLoading}
              onClick={handleSave}>
              Add service area
            </CustomButton>
          </div>
        </If>
      </div>
    </CustomModal>
  );
};

export default ServiceAreaCreateModal;
